import { DatePipe } from '@angular/common';
import { Component, Input, SimpleChanges } from '@angular/core';
import { base64ToArrayBuffer, saveByteArray } from 'app/helpers/blobDownload';
import { ExposedReadDocumentRes } from 'app/interfaces/responses/Exposed/exposedReadDocumentRes';
import { ExposedReadMortgageEligibilityInfoRes } from 'app/interfaces/responses/Exposed/exposedReadMortgageEligibilityInfoRes';
import { Option } from 'app/interfaces/support/option';
import { MortgageEligibilityInfoService } from 'app/services/api/mortgageEligibilityInfo/mortgageEligibilitInfo.service';
import { finalize } from 'rxjs';

interface PropertyData {
    documentOptions: Option<number>[];
    selectedDocument: number;
    pdfSrc: string;
    loading: boolean;
    error: boolean;
}

@Component({
    selector: 'mortgage-eligibility-form-property-historical-documents',
    templateUrl: './loans-mortgage-eligibility-form-property-historical-documents.component.html',
    styleUrls: ['./loans-mortgage-eligibility-form-property-historical-documents.component.scss'],
})
export class LoansMortgageEligibilityFormPropertyHistoricalDocumentsComponent {
    @Input() historicalDocumentsProperty: string;
    @Input() documentProperty: string;
    @Input() data: ExposedReadMortgageEligibilityInfoRes;
    @Input() noDataLabel: string;
    @Input() fileName: string;

    propertyDataMap: { [propertyId: number]: PropertyData } = {};
    documentMap: { [documentId: number]: ExposedReadDocumentRes } = {};

    constructor(
        private datePipe: DatePipe,
        private mortgageEligibilityInfoService: MortgageEligibilityInfoService
    ) {}

    // dataVersion is for making sure older HTTP response doesn't overwrite newer data
    private dataVersion = 0;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['data'] || changes['documentProperty'] || changes['historicalDocumentProperty']) {
            this.dataVersion++;
            this.initData();
        }
    }

    private initData(): void {
        const previousPropertyData = this.propertyDataMap;
        const previousDocuments = this.documentMap;

        const propertyDataMap: { [propertyId: number]: PropertyData } = {};
        const documentMap: { [documentId: number]: ExposedReadDocumentRes } = {};

        for (const property of this.data?.properties ?? []) {
            const currentDocument = property[this.documentProperty];

            if (!currentDocument?.documentId) {
                continue;
            }

            const historicalDocuments = property[this.historicalDocumentsProperty] ?? [];

            const documentOptions: Option<number>[] = historicalDocuments.map((doc) => ({
                label: this.datePipe.transform(doc.timeStamp, 'dd.MM.yyyy HH:mm:ss') ?? '',
                value: doc.documentId,
            }));

            const previousSelection = previousPropertyData[property.id]?.selectedDocument;

            const validPreviousSelection =
                previousSelection != null &&
                (previousSelection === currentDocument.documentId || documentOptions.some((option) => option.value === previousSelection));

            const selectedDocument = validPreviousSelection ? previousSelection : currentDocument.documentId;

            if (currentDocument.base64File) {
                documentMap[currentDocument.documentId] = currentDocument;
            }

            const selectedDocumentData = documentMap[selectedDocument] ?? previousDocuments[selectedDocument];

            if (selectedDocumentData?.base64File) {
                documentMap[selectedDocument] = selectedDocumentData;
            }

            propertyDataMap[property.id] = {
                documentOptions,
                selectedDocument,
                pdfSrc: selectedDocumentData?.base64File ? this.getDocumentSrc(selectedDocumentData) : '',
                loading: false,
                error: false,
            };
        }

        this.propertyDataMap = propertyDataMap;
        this.documentMap = documentMap;

        // Fetch documents that exist but were not included as PDF content.
        for (const propertyId of Object.keys(propertyDataMap).map(Number)) {
            if (!propertyDataMap[propertyId].pdfSrc) {
                this.loadSelectedDocument(propertyId);
            }
        }
    }

    onDocumentChanged(propertyId: number): void {
        this.loadSelectedDocument(propertyId);
    }

    private loadSelectedDocument(propertyId: number): void {
        const propertyData = this.propertyDataMap[propertyId];

        if (!propertyData) {
            return;
        }

        const documentId = propertyData.selectedDocument;
        const infoId = this.data.id;
        const version = this.dataVersion;

        const cachedDocument = this.documentMap[documentId];

        if (cachedDocument?.base64File) {
            propertyData.pdfSrc = this.getDocumentSrc(cachedDocument);
            propertyData.error = false;
            return;
        }

        propertyData.loading = true;
        propertyData.error = false;
        propertyData.pdfSrc = '';

        this.mortgageEligibilityInfoService
            .getMortgageEligibilityInfoPropertyDocument(infoId, propertyId, documentId)
            .pipe(
                finalize(() => {
                    const currentPropertyData = this.propertyDataMap[propertyId];

                    if (this.dataVersion === version && currentPropertyData?.selectedDocument === documentId) {
                        currentPropertyData.loading = false;

                        if (!currentPropertyData.pdfSrc) {
                            currentPropertyData.error = true;
                        }
                    }
                })
            )
            .subscribe({
                next: (document) => {
                    if (
                        this.dataVersion !== version ||
                        this.data?.id !== infoId ||
                        this.propertyDataMap[propertyId]?.selectedDocument !== documentId
                    ) {
                        return;
                    }

                    if (!document?.base64File) {
                        this.propertyDataMap[propertyId].error = true;
                        return;
                    }

                    this.documentMap[documentId] = document;
                    this.propertyDataMap[propertyId].pdfSrc = this.getDocumentSrc(document);
                },
                error: () => {
                    if (this.dataVersion === version && this.propertyDataMap[propertyId]?.selectedDocument === documentId) {
                        this.propertyDataMap[propertyId].error = true;
                    }
                },
            });
    }

    retryDocument(propertyId: number): void {
        const propertyData = this.propertyDataMap[propertyId];

        if (!propertyData || propertyData.loading) {
            return;
        }

        delete this.documentMap[propertyData.selectedDocument];

        propertyData.pdfSrc = '';
        propertyData.error = false;

        this.loadSelectedDocument(propertyId);
    }

    onPdfError(propertyId: number): void {
        const propertyData = this.propertyDataMap[propertyId];

        if (propertyData) {
            propertyData.error = true;
        }
    }

    getDocumentSrc = (document: ExposedReadDocumentRes) => {
        return 'data:application/pdf;base64,' + document.base64File;
    };

    downloadDocument = (propertyId: number) => {
        let document = this.documentMap[this.propertyDataMap[propertyId].selectedDocument];
        var sampleArr = base64ToArrayBuffer(document.base64File);
        var propertyNumber = this.data.properties.find((p) => p.id === propertyId).propertyNumber;
        saveByteArray(`${this.fileName}_${propertyNumber}.pdf`, sampleArr);
    };
}
